import { useMutation, useQueryClient } from '@tanstack/react-query'

import { invitationsApi, type InviteNode } from '@/api/invitationsApi'
import { staffApi } from '@/api/staffApi'
import { useToast, type ToastOptions } from '@/components/ui/Toast'
import type { InviteInput } from '@/features/invitations/schemas/invitationSchemas'
import { queryKeys } from '@/lib/query/queryKeys'

/** Each action confirms itself and refreshes every admin read; errors stay in the dialog. */
function useAdminMutation<TInput, TResult>(
  mutationFn: (input: TInput) => Promise<TResult>,
  toast?: (input: TInput) => ToastOptions,
) {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn,
    onSuccess: (_data, input) => {
      if (toast) showToast(toast(input))
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.all })
    },
  })
}

export const useBlockStaff = () =>
  useAdminMutation(
    (member: { publicId: string; displayName: string }) => staffApi.block(member.publicId),
    (member) => ({ title: 'Konto gesperrt', description: member.displayName }),
  )

export const useUnblockStaff = () =>
  useAdminMutation(
    (member: { publicId: string; displayName: string }) => staffApi.unblock(member.publicId),
    (member) => ({ title: 'Konto entsperrt', description: member.displayName }),
  )

export const useRevokeInvitation = () =>
  useAdminMutation(
    (invitation: { publicId: string; email: string }) => invitationsApi.revoke(invitation.publicId),
    (invitation) => ({ title: 'Einladung widerrufen', description: invitation.email }),
  )

/** No toast: the one-time link is what the person needs to see. */
export const useInviteAt = () =>
  useAdminMutation(({ input, node }: { input: InviteInput; node: InviteNode }) =>
    invitationsApi.createAt(input, node),
  )
